import { Component, OnDestroy, OnInit, effect, input, output, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import type { AppleBundleIdentifier } from '../../types/app-lock.types';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PaneComponent } from '@rocksoft/cdk';
import { Observable, ReplaySubject, Subject, Subscription, combineLatest } from 'rxjs';
import { filter, map, scan, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'era-app-lock-selection',
  templateUrl: './app-lock-selection.component.html',
  styleUrls: ['./app-lock-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PaneComponent, ReactiveFormsModule, FaIconComponent],
})
export class AppLockSelectionComponent implements OnInit, OnDestroy {
  public readonly form: UntypedFormGroup;

  private readonly setting$: Subject<AppleBundleIdentifier> = new ReplaySubject<AppleBundleIdentifier>(1);
  private readonly settings$: Subject<AppleBundleIdentifier[]> = new ReplaySubject<AppleBundleIdentifier[]>(1);
  private readonly editMode$: Subject<boolean> = new ReplaySubject<boolean>(1);
  private readonly overrideMode$: Subject<boolean> = new ReplaySubject<boolean>(1);
  private readonly subscriptions: Subscription[] = [];

  readonly canDelete = input(false);
  readonly inheritPossible = input(true);
  readonly allowEdit = input(false);
  readonly overrideModeInput = input(false);
  readonly settings = input.required<AppleBundleIdentifier[]>();
  readonly settingInput = input.required<AppleBundleIdentifier>();

  readonly settingChange = output<AppleBundleIdentifier | null>();
  readonly overrideRemoved = output<AppleBundleIdentifier | null | undefined>();
  readonly deleteEvent = output();

  overrideMode = false;
  setting: AppleBundleIdentifier | undefined | null = undefined;

  editMode = false;

  constructor() {
    // Moved from edit mode enabled to edit mode disabled
    const disabledEditMode: Observable<void> = this.editMode$.pipe(
      scan<boolean, [boolean, boolean]>(([, prev], curr) => [prev, curr], [false, false]),
      filter(([prev, curr]) => prev && !curr),
      map(() => undefined)
    );

    // Moved from override mode enabled to override mode disabled
    const disabledOverrideMode: Observable<void> = this.overrideMode$.pipe(
      scan<boolean, [boolean, boolean]>(([, prev], curr) => [prev, curr], [false, false]),
      filter(([prev, curr]) => prev && !curr),
      map(() => undefined)
    );

    this.form = new UntypedFormGroup({
      setting: new UntypedFormControl(),
    });

    // The selected setting will be:
    // - If override mode is enabled AND the current setting does exist on this level -> the current setting
    // - If override mode is disabled OR the current setting does not exist on this level -> no setting
    combineLatest([this.setting$, this.settings$, this.overrideMode$])
      .pipe(
        map(([setting, settings, overrideMode]) => {
          // The template always picks the first value as the default
          if (overrideMode && setting === undefined && settings.length > 0) {
            return settings[0];
          }

          const index = settings.findIndex((s) => s === setting);
          const settingsContainsSetting = index > -1;

          return overrideMode && settingsContainsSetting ? setting : undefined;
        })
      )
      .subscribe((setting) => {
        this.form.reset({ setting });
      });

    // Auto disable edit mode (and thus save the changes) when overrideMode is disabled
    this.subscriptions.push(
      disabledOverrideMode
        .pipe(
          withLatestFrom(this.editMode$),
          filter(([, editMode]) => editMode)
        )
        .subscribe(() => {
          this.toggleEditMode();
        })
    );

    this.subscriptions.push(
      this.overrideMode$.subscribe(() => {
        this.applyOverrideMode();
      })
    );
    this.subscriptions.push(
      disabledEditMode.subscribe(() => {
        this.saveChanges();
      })
    );

    effect(() => {
      const settingValue = this.settingInput();
      this.setting = settingValue;
      this.setting$.next(settingValue);
    });

    effect(() => {
      const settings = this.settings();
      if (settings) {
        this.settings$.next(settings || []);
      }
    });

    effect(() => {
      const overrideMode = this.overrideModeInput();
      this.overrideMode = overrideMode;
      this.overrideMode$.next(overrideMode);
    });
  }

  ngOnInit() {
    // Init observables with default values - effects handle the rest
    this.editMode$.next(this.editMode);
  }

  public toggleEditMode() {
    if (!this.allowEdit()) {
      return;
    }

    this.editMode = !this.editMode;
    this.editMode$.next(this.editMode);
  }

  public toggleOverrideMode() {
    this.overrideMode = !this.overrideMode;
    this.overrideMode$.next(this.overrideMode);
  }

  public delete() {
    if (this.canDelete()) {
      this.deleteEvent.emit();
      this.setting = null;
    }
  }

  private saveChanges() {
    if (this.overrideMode) {
      this.settingChange.emit(this.form.get('setting')?.value);
    } else {
      this.settingChange.emit(null);
      this.overrideRemoved.emit(this.setting);
    }
  }

  private applyOverrideMode() {
    if (this.overrideMode) {
      this.form.get('setting')?.enable();
    } else {
      this.form.get('setting')?.disable();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}
